import React, { useEffect, useState } from 'react';
import { Card, Button, Form, Row, Col, Table, Badge, Modal, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import adminService from '../../services/adminService';
import { useAuth } from '../../contexts/AuthContext';

interface Coupon {
  id: number;
  code: string;
  name: string;
  description?: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  max_discount_amount?: number;
  max_usage_count?: number;
  max_usage_per_user: number;
  current_usage_count: number;
  min_order_value: number;
  include_concessions: boolean;
  valid_from: string;
  valid_to: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const Coupons: React.FC = () => {
  const { hasPermission } = useAuth();
  const canReadCoupons = hasPermission('coupons:read');
  const canWriteCoupons = hasPermission('coupons:write');
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    discount_type: 'percentage' as 'percentage' | 'fixed',
    discount_value: undefined as number | undefined,
  max_discount_amount: undefined as number | undefined,
  max_usage_count: undefined as number | undefined,
  max_usage_per_user: 1 as number | undefined,
  min_order_value: undefined as number | undefined,
    include_concessions: false,
    valid_from: '',
    valid_to: '',
    is_active: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');

  useEffect(() => {
    fetchCoupons();
  }, [searchTerm, filterActive]);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const response = await adminService.getCoupons({
        active_only: filterActive === 'active' ? 'true' : undefined,
        search: searchTerm || undefined,
      });
      if (response.success && response.data) {
        setCoupons(response.data);
      } else {
        toast.error(response.error || 'Failed to load coupons');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const handleNumberChange = (field: string, value: string) => {
    handleInputChange(
      field,
      value === '' ? undefined : Number(value)
    );
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Coupon code is required';
    } else if (formData.code.length < 3) {
      newErrors.code = 'Coupon code must be at least 3 characters';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Coupon name is required';
    }

    if (formData.discount_value == null || formData.discount_value <= 0) {
      newErrors.discount_value = 'Discount value must be greater than 0';
    }
    
    if (
      formData.discount_type === 'percentage' &&
      (formData.max_discount_amount == null || formData.max_discount_amount <= 0)
    ) {
      newErrors.max_discount_amount = 'Max discount amount is required';
    }
    
    if (
      formData.max_usage_per_user == null ||
      formData.max_usage_per_user < 0
    ) {
      newErrors.max_usage_per_user = 'Max usage per user must be >= 0';
    }
    
    if (
      formData.min_order_value == null ||
      formData.min_order_value < 0
    ) {
      newErrors.min_order_value = 'Min order value must be >= 0';
    }

    if (!formData.valid_from) {
      newErrors.valid_from = 'Valid from date is required';
    }

    if (!formData.valid_to) {
      newErrors.valid_to = 'Valid to date is required';
    }

    if (formData.valid_from && formData.valid_to) {
      const fromDate = new Date(formData.valid_from);
      const toDate = new Date(formData.valid_to);
      if (toDate <= fromDate) {
        newErrors.valid_to = 'Valid to date must be after valid from date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpenModal = (coupon?: Coupon) => {
    if (coupon) {
      setEditingCoupon(coupon);
  
      setFormData({
        code: coupon.code,
        name: coupon.name,
        description: coupon.description || '',
        discount_type: coupon.discount_type,
  
        discount_value: coupon.discount_value ?? undefined,
        max_discount_amount: coupon.max_discount_amount ?? undefined,
        max_usage_count: coupon.max_usage_count ?? undefined,
        max_usage_per_user: coupon.max_usage_per_user ?? 1,
        min_order_value: coupon.min_order_value ?? undefined,
  
        include_concessions: coupon.include_concessions,
  
        valid_from: coupon.valid_from
          ? new Date(coupon.valid_from).toISOString().slice(0, 16)
          : '',
  
        valid_to: coupon.valid_to
          ? new Date(coupon.valid_to).toISOString().slice(0, 16)
          : '',
  
        is_active: coupon.is_active,
      });
    } else {
      setEditingCoupon(null);
  
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
  
      const nextMonth = new Date();
      nextMonth.setMonth(nextMonth.getMonth() + 1);
  
      setFormData({
        code: '',
        name: '',
        description: '',
        discount_type: 'percentage',
  
        discount_value: undefined,
        max_discount_amount: undefined,
        max_usage_count: undefined,
        max_usage_per_user: 1,
        min_order_value: undefined,
  
        include_concessions: false,
  
        valid_from: tomorrow.toISOString().slice(0, 16),
        valid_to: nextMonth.toISOString().slice(0, 16),
  
        is_active: true,
      });
    }
  
    setErrors({});
    setShowModal(true);
  };
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCoupon(null);
    setErrors({});
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSave = async () => {
    if (!canWriteCoupons) {
      toast.error('You do not have permission to modify coupons');
      return;
    }
  
    if (!validateForm()) {
      return;
    }
  
    try {
      setSaving(true);
  
      const couponData: any = {
        code: formData.code.toUpperCase().trim(),
        name: formData.name.trim(),
        description: formData.description?.trim() || undefined,
  
        discount_type: formData.discount_type,
        discount_value: formData.discount_value,
  
        max_discount_amount:
          formData.discount_type === 'percentage'
            ? formData.max_discount_amount
            : undefined,
  
        max_usage_count: formData.max_usage_count,
        max_usage_per_user: formData.max_usage_per_user,
  
        min_order_value: formData.min_order_value,
        include_concessions: formData.include_concessions,
  
        valid_from: new Date(formData.valid_from).toISOString(),
        valid_to: new Date(formData.valid_to).toISOString(),
  
        is_active: formData.is_active,
      };
  
      // Remove undefined fields (clean payload)
      Object.keys(couponData).forEach((key) => {
        if (couponData[key] === undefined) {
          delete couponData[key];
        }
      });
  
      if (editingCoupon) {
        delete couponData.code; // prevent editing code
  
        const response = await adminService.updateCoupon(
          editingCoupon.id,
          couponData
        );
  
        if (response.success) {
          toast.success('Coupon updated successfully');
          fetchCoupons();
          handleCloseModal();
        } else {
          toast.error(response.error || 'Failed to update coupon');
        }
      } else {
        const response = await adminService.createCoupon(couponData);
  
        if (response.success) {
          toast.success('Coupon created successfully');
          fetchCoupons();
          handleCloseModal();
        } else {
          toast.error(response.error || 'Failed to create coupon');
        }
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to save coupon');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number, code: string) => {
    if (!window.confirm(`Are you sure you want to delete coupon "${code}"?`)) {
      return;
    }

    try {
      const response = await adminService.deleteCoupon(id);
      if (response.success) {
        toast.success('Coupon deleted successfully');
        fetchCoupons();
      } else {
        toast.error(response.error || 'Failed to delete coupon');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete coupon');
    }
  };

  const handleToggleActive = async (coupon: Coupon) => {
    try {
      const response = await adminService.updateCoupon(coupon.id, {
        is_active: !coupon.is_active,
      });
      if (response.success) {
        toast.success(`Coupon ${!coupon.is_active ? 'activated' : 'deactivated'} successfully`);
        fetchCoupons();
      } else {
        toast.error(response.error || 'Failed to update coupon');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to update coupon');
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const isCouponValid = (coupon: Coupon): boolean => {
    const now = new Date();
    const validFrom = new Date(coupon.valid_from);
    const validTo = new Date(coupon.valid_to);
    return now >= validFrom && now <= validTo && coupon.is_active;
  };

  return (
    <>
      <Helmet>
        <title>Coupons - MyCinemas Admin</title>
      </Helmet>

      <div className="container-fluid py-4">
        {!canReadCoupons && (
          <Alert variant="warning">
            <i className="bi bi-exclamation-triangle me-2"></i>
            You do not have permission to view coupons. Please contact an administrator.
          </Alert>
        )}
        
        {canReadCoupons && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2>Discount Coupons</h2>
              {canWriteCoupons && (
                <Button variant="primary" onClick={() => handleOpenModal()}>
                  <i className="bi bi-plus-circle me-2"></i>
                  Create Coupon
                </Button>
              )}
            </div>

        <Card>
          <Card.Body>
            {/* Filters */}
            <Row className="mb-3">
              <Col md={6}>
                <InputGroup>
                  <InputGroup.Text>
                    <i className="bi bi-search"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search by code or name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={filterActive}
                  onChange={(e) => setFilterActive(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <Button variant="outline-secondary" onClick={fetchCoupons}>
                  <i className="bi bi-arrow-clockwise"></i> Refresh
                </Button>
              </Col>
            </Row>

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Loading coupons...</p>
              </div>
            ) : coupons.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted">No coupons found.</p>
              </div>
            ) : (
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Discount</th>
                    <th>Usage</th>
                    <th>Valid Period</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.map((coupon) => (
                    <tr key={coupon.id}>
                      <td>
                        <Badge bg="info" className="fs-6">{coupon.code}</Badge>
                      </td>
                      <td>{coupon.name}</td>
                      <td>
                        {coupon.discount_type === 'percentage' ? (
                          <>{coupon.discount_value}% (Max ₹{coupon.max_discount_amount})</>
                        ) : (
                          <>₹{coupon.discount_value}</>
                        )}
                      </td>
                      <td>
                        {coupon.current_usage_count}
                        {coupon.max_usage_count ? ` / ${coupon.max_usage_count}` : ' / ∞'}
                      </td>
                      <td>
                        <small>
                          {formatDate(coupon.valid_from)}<br />
                          to {formatDate(coupon.valid_to)}
                        </small>
                      </td>
                      <td>
                        <Badge bg={isCouponValid(coupon) ? 'success' : 'secondary'}>
                          {isCouponValid(coupon) ? 'Valid' : 'Invalid'}
                        </Badge>
                        {' '}
                        <Badge bg={coupon.is_active ? 'success' : 'secondary'}>
                          {coupon.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td>
                        {canWriteCoupons && (
                          <>
                            <Button
                              variant="link"
                              size="sm"
                              onClick={() => handleOpenModal(coupon)}
                              className="me-2"
                            >
                              <i className="bi bi-pencil"></i> Edit
                            </Button>
                            <Button
                              variant="link"
                              size="sm"
                              onClick={() => handleToggleActive(coupon)}
                              className="me-2"
                            >
                              <i className={`bi bi-toggle-${coupon.is_active ? 'on' : 'off'}`}></i>{' '}
                              {coupon.is_active ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button
                              variant="link"
                              size="sm"
                              className="text-danger"
                              onClick={() => handleDelete(coupon.id, coupon.code)}
                            >
                              <i className="bi bi-trash"></i> Delete
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Coupon Code *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.code}
                    onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                    isInvalid={!!errors.code}
                    disabled={!!editingCoupon}
                    placeholder="e.g., SAVE20"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.code}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    {editingCoupon ? 'Code cannot be changed after creation' : 'Unique code for the coupon'}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Coupon Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    isInvalid={!!errors.name}
                    placeholder="e.g., Summer Sale 20% Off"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Optional description of the coupon"
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Discount Type *</Form.Label>
                  <Form.Select
                    value={formData.discount_type}
                    onChange={(e) => handleInputChange('discount_type', e.target.value)}
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {formData.discount_type === 'percentage' ? 'Discount % *' : 'Discount Amount (₹) *'}
                  </Form.Label>
                  <Form.Control
  type="number"
  step={formData.discount_type === 'percentage' ? '0.01' : '1'}
  min="0"
  max={formData.discount_type === 'percentage' ? '100' : undefined}
  value={formData.discount_value ?? ''}
  onChange={(e) => handleNumberChange('discount_value', e.target.value)}
  isInvalid={!!errors.discount_value}
/>
                  <Form.Control.Feedback type="invalid">
                    {errors.discount_value}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              {formData.discount_type === 'percentage' && (
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Max Discount Amount (₹) *</Form.Label>
                    <Form.Control
  type="number"
  step="1"
  min="0"
  value={formData.max_discount_amount ?? ''}
  onChange={(e) => handleNumberChange('max_discount_amount', e.target.value)}
  isInvalid={!!errors.max_discount_amount}
/>
                    <Form.Control.Feedback type="invalid">
                      {errors.max_discount_amount}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      Maximum discount cap for percentage discounts
                    </Form.Text>
                  </Form.Group>
                </Col>
              )}
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Max Total Usage</Form.Label>
                  <Form.Control
  type="number"
  step="1"
  min="0"
  value={formData.max_usage_count ?? ''}
  onChange={(e) => handleNumberChange('max_usage_count', e.target.value)}
  placeholder="Leave empty for unlimited"
  isInvalid={!!errors.max_usage_count}
/>
                  <Form.Control.Feedback type="invalid">
                    {errors.max_usage_count}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Total number of times this coupon can be used (leave empty for unlimited)
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Max Usage Per User *</Form.Label>
                  <Form.Control
  type="number"
  step="1"
  min="0"
  value={formData.max_usage_per_user ?? ''}
  onChange={(e) => handleNumberChange('max_usage_per_user', e.target.value)}
  isInvalid={!!errors.max_usage_per_user}
/>
                  <Form.Control.Feedback type="invalid">
                    {errors.max_usage_per_user}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted">
                    Maximum times a single user can use this coupon
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Minimum Order Value (₹) *</Form.Label>
                  <Form.Control
  type="number"
  step="0.01"
  min="0"
  value={formData.min_order_value ?? ''}
  onChange={(e) => handleNumberChange('min_order_value', e.target.value)}
  isInvalid={!!errors.min_order_value}
/>
                  <Form.Control.Feedback type="invalid">
                    {errors.min_order_value}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Include Concessions in Order Value</Form.Label>
                  <Form.Check
                    type="switch"
                    id="include_concessions"
                    label="Include concessions when calculating order value"
                    checked={formData.include_concessions}
                    onChange={(e) => handleInputChange('include_concessions', e.target.checked)}
                  />
                  <Form.Text className="text-muted">
                    If enabled, concession items will be included in the minimum order value calculation
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Valid From *</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={formData.valid_from}
                    onChange={(e) => handleInputChange('valid_from', e.target.value)}
                    isInvalid={!!errors.valid_from}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.valid_from}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Valid To *</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={formData.valid_to}
                    onChange={(e) => handleInputChange('valid_to', e.target.value)}
                    isInvalid={!!errors.valid_to}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.valid_to}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="is_active"
                label="Active"
                checked={formData.is_active}
                onChange={(e) => handleInputChange('is_active', e.target.checked)}
              />
              <Form.Text className="text-muted">
                Inactive coupons cannot be used
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Coupons;

